import { useRef, useState } from 'react';
import { Dialog, Popover } from 'react-aria-components';
import { plural } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import InputNumber from '@/design_system/InputNumber';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconEdit from '@/icons/Edit.svg';
import { useUpdateManualExchangeDueDays } from '@/models/article';
import { getDefaultExchangeDuration } from '@/models/workflow';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import './ExchangeDuration.css';

const { block } = createBEMClasses('exchange-duration-tooltip');

const ExchangeDurationTooltip = ({ defaultWorkflowValue }: { defaultWorkflowValue: number }) => {
  const { article } = useArticleContext();
  const { t } = useLingui();
  const [isInvalid, setIsInvalid] = useState(false);
  const [manualExchangeDueDaysValue, setManualExchangeDueDaysValue] = useState(
    article.manualExchangeDueDays
  );

  const { mutateAsync: updateManualExchangeDueDays } = useUpdateManualExchangeDueDays({
    articleId: article.id,
  });

  const placeholder = t({
    id: 'exchange-duration.days',
    message: plural(Number(defaultWorkflowValue), {
      one: '# day',
      other: '# days',
    }),
  });

  return (
    <Dialog
      className={block()}
      aria-label={t({
        id: 'exchange-duration.popover',
        message: 'Set the estimated time needed to complete the exchange (in working days).',
      })}
    >
      <Stack gap={16}>
        <Paragraph>
          <Trans id="exchange-duration.popover">
            Set the estimated time needed to complete the exchange (in working days).
          </Trans>
        </Paragraph>
        <InputNumber
          placeholder={placeholder}
          size="small"
          isInvalid={isInvalid}
          value={manualExchangeDueDaysValue ?? NaN}
          onChange={async (value) => {
            if (!isNaN(value) && (!Number.isInteger(value) || value < 0)) {
              return setIsInvalid(true);
            }
            setManualExchangeDueDaysValue(isNaN(value) ? null : value);
            setIsInvalid(false);

            try {
              await updateManualExchangeDueDays({
                manualExchangeDueDays: isNaN(value) ? null : value,
              });
            } catch {
              setManualExchangeDueDaysValue(article.manualExchangeDueDays);
            }
          }}
        />
      </Stack>
    </Dialog>
  );
};

export const ExchangeDuration = () => {
  const popoverAnchorRef = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);
  const { request, workflow } = useRequestContext();
  const { article } = useArticleContext();
  const { t } = useLingui();
  const { isMobile } = useViewPort();

  const defaultWorkflowValue = getDefaultExchangeDuration({ workflow, request });

  return (
    <Stack row={isMobile} alignItems={isMobile ? 'center' : 'end'} justifyContent="space-between">
      <Paragraph size="200" color="secondary">
        <Trans id="exchange-duration.label">Exchange duration (est.)</Trans>
      </Paragraph>
      <Stack row justifyContent={'end'} gap={8} ref={popoverAnchorRef}>
        <Paragraph>
          <Plural
            id="repair-duration.days"
            value={article.manualExchangeDueDays ?? defaultWorkflowValue}
            one="# day"
            other="# days"
          />
        </Paragraph>
        <Button
          ariaLabel={t({ id: 'exchange-duration.buttonLabel', message: 'Edit exchange duration' })}
          iconOnly
          size="small"
          variant="secondary"
          onPress={() => setOpen(true)}
        >
          <IconEdit />
        </Button>

        <Popover
          placement="bottom left"
          triggerRef={popoverAnchorRef}
          isOpen={isOpen}
          onOpenChange={setOpen}
          offset={4}
        >
          <ExchangeDurationTooltip defaultWorkflowValue={defaultWorkflowValue} />
        </Popover>
      </Stack>
    </Stack>
  );
};
